/**
 * Subscription bonus rule:
 * - If cart total > min AND cart contains at least one subscription (selling_plan_allocation),
 *   ensure bonus product X is in cart with quantity = 1.
 * - Customer cannot increase quantity of bonus product.
 * - If subscription removed OR total drops below min, bonus product is removed.
 * - If customer tries to add bonus product directly without subscription, it is removed.
 */
(() => {
  const cfg = window?.themeHDN?.subscriptionBonusRule || {};
  const enabled = Boolean(cfg.enabled);
  const bonusVariantId = Number(cfg.bonusVariantId || 0);
  const minTotalCents = Number(cfg.minTotalCents || 2000);

  if (!enabled || !bonusVariantId) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  const cartUrl = `${root}cart.js`;
  const updateUrl = `${root}cart/update.js`;

  let enforcing = false;
  let pending = false;

  function hasSubscriptionItem(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((it) => {
      if (!it) return false;
      if (Number(it.variant_id) === bonusVariantId) return false;
      return Boolean(it.selling_plan_allocation);
    });
  }

  function findBonusLine(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find((it) => Number(it?.variant_id) === bonusVariantId) || null;
  }

  async function fetchCart() {
    const res = await fetch(cartUrl, { headers: { 'Accept': 'application/json' } });
    return await res.json();
  }

  function collectSections() {
    const sections = [];
    document.dispatchEvent(new CustomEvent('cart:push:section.id', { bubbles: true, detail: { sections } }));
    return sections;
  }

  async function updateVariantQty(variantId, quantity) {
    const sections = collectSections();
    const res = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: { [String(variantId)]: quantity },
        sections: sections.join(',')
      })
    });
    return await res.json().catch(() => ({}));
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'bonus:enforce', cart }
    }));
  }

  function lockBonusQtyUI() {
    // Inject lightweight CSS once
    if (!document.getElementById('subscription-bonus-rule-style')) {
      const style = document.createElement('style');
      style.id = 'subscription-bonus-rule-style';
      style.textContent = `
        hdt-line-item.is-bonus-locked .hdt-quantity__button.is--plus { pointer-events: none !important; opacity: .35 !important; }
        hdt-line-item.is-bonus-locked input.hdt-quantity-input { pointer-events: none !important; opacity: .85 !important; }
      `;
      document.head.appendChild(style);
    }

    const selector = `hdt-line-item[data-variant-id="${bonusVariantId}"]`;
    document.querySelectorAll(selector).forEach((line) => {
      line.classList.add('is-bonus-locked');
      const input = line.querySelector('input.hdt-quantity-input[name="updates[]"]');
      if (input) {
        input.min = '1';
        input.max = '1';
        input.value = '1';
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
      }
      const plus = line.querySelector('button.hdt-quantity__button.is--plus[name="plus"]');
      if (plus) plus.disabled = true;
    });
  }

  async function enforce(cartFromEvent) {
    if (enforcing) {
      pending = true;
      return;
    }
    enforcing = true;
    pending = false;

    try {
      let cart = cartFromEvent || await fetchCart();

      // Use subtotal-like number if present; otherwise total_price
      const totalCents = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
      const qualifiesTotal = totalCents > minTotalCents;
      const qualifiesSubscription = hasSubscriptionItem(cart);
      const shouldHaveBonus = qualifiesTotal && qualifiesSubscription;

      const bonusLine = findBonusLine(cart);

      let mutated = false;
      let mutationResponse = null;

      if (shouldHaveBonus) {
        if (!bonusLine) {
          mutationResponse = await updateVariantQty(bonusVariantId, 1);
          mutated = true;
        } else if (Number(bonusLine.quantity) !== 1) {
          // Enforce qty=1 (prevents increasing quantity)
          mutationResponse = await updateVariantQty(bonusVariantId, 1);
          mutated = true;
        }
      } else {
        // Not eligible => bonus must be removed (also removes direct-add attempts)
        if (bonusLine) {
          mutationResponse = await updateVariantQty(bonusVariantId, 0);
          mutated = true;
        }
      }

      if (mutated) {
        // Minimize delay: reuse HTML sections returned by the mutation endpoint.
        // Fallback: fetch cart state if response isn't a full cart object.
        const maybeCart = mutationResponse && typeof mutationResponse === 'object' ? mutationResponse : null;
        cart = (maybeCart && typeof maybeCart.item_count === 'number') ? maybeCart : await fetchCart();
        if (mutationResponse?.sections) cart.sections = mutationResponse.sections;
        dispatchCartChange(cart);
      }

      // Always lock UI if the line is present (or will be present)
      lockBonusQtyUI();
    } catch (err) {
      // Fail-safe: never block cart UX
      console.error('[subscription-bonus-rule] error', err);
    } finally {
      enforcing = false;
      if (pending) enforce();
    }
  }

  // Run after any theme cart change (add/update/remove)
  document.addEventListener('cart:change', (e) => {
    if (e?.detail?.baseEvent === 'bonus:enforce') return;
    enforce(e?.detail?.cart);
  });

  // When theme forces refresh, re-check rules
  document.addEventListener('cart:refresh', () => enforce());

  // First load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enforce());
  } else {
    enforce();
  }
})();

